import React, { useState, useContext, useEffect } from "react";
import "./Demo-admin.css";
import AdminNav from "../Navbars/AdminNav";
import Footer from "../Footer";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../UserContext";
import { toast } from "react-toastify";

const DemoAdmin = () => {
  const [schedule, setSchedule] = useState({
    studentId: '',
    date: '',
    time: '12:00 AM'
  });
  const [students, setStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { isAdmin, setUserRole, setUserInfo, userInfo } = useContext(UserContext);
  const navigate = useNavigate();

  // Generate time options in 12-hour format with AM/PM
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour % 12 || 12;
        const timeString = `${displayHour.toString().padStart(2, ' ')}:${minute.toString().padStart(2, '0')} ${period}`;
        times.push(timeString);
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Fetch students from backend
  useEffect(() => {
    const fetchStudents = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/students`);
        if (!response.ok) {
          throw new Error('Failed to fetch students');
        }
        const data = await response.json();
        setStudents(data);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching students:', error);
        setIsLoading(false);
      }
    };

    fetchStudents();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSchedule(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTimeChange = (time) => {
    setSchedule(prev => ({
      ...prev,
      time
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!schedule.studentId) {
      toast.warning("Please select a student", {
        position: toast.POSITION.TOP_RIGHT
      });
      return;
    }

    try {
      // Format date as YYYY-MM-DD for final_date
      const formattedDate = schedule.date;
      
      // Keep time as is (with AM/PM) for final_time
      const formattedTime = schedule.time;

      const response = await fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/updateFinalSchedule`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentId: schedule.studentId,
          finalDate: formattedDate,
          finalTime: formattedTime
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to update schedule');
      }

      toast.success(`Demo scheduled for student ID ${schedule.studentId} on ${schedule.date} at ${schedule.time}`, {
        position: toast.POSITION.TOP_RIGHT
      });
      
      // Reset form
      setSchedule({
        studentId: '',
        date: '',
        time: '12:00 AM'
      });

    } catch (error) {
      console.error('Error updating schedule:', error);
      toast.error(error.message || 'Failed to update schedule. Please try again.', {
        position: toast.POSITION.TOP_RIGHT
      });
    }
  };

  // Handle logout
  const onLogout = () => {
    fetch(`${process.env.REACT_APP_LOCAL_SERVER_API}/logout`, {
      credentials: "include",
      method: "POST",
    })
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.error("Logout failed:", error);
      });
    setUserInfo(null);
    setUserRole("guest");
  };

  return (
    <>
      {/* Admin Navigation Header */}
      {userInfo?.username && isAdmin && (
        <AdminNav username={userInfo.username} onLogout={onLogout} />
      )}
      
      {/* Page Header */}
      <header className="admin-page-header">
        <div className="admin-header-content">
          <h1>Demo Schedule</h1>
          <p>Manage and schedule demo sessions for students</p>
        </div>
      </header>

      {/* Main Content */}
      <div className="demo-schedule-container">
        <form onSubmit={handleSubmit} className="schedule-form">
          <div className="form-group">
            <label htmlFor="studentId">Select Student*</label>
            {isLoading ? (
              <select disabled>
                <option>Loading students...</option>
              </select>
            ) : (
              <select
                id="studentId"
                name="studentId"
                value={schedule.studentId}
                onChange={handleChange}
                required
              >
                <option value="">Select a student</option>
                {students.map(student => (
                  <option key={student.id} value={student.id}>
                    {student.s_name} (ID: {student.id}) - {student.course}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="date">Select Date*</label>
            <input
              type="date"
              id="date"
              name="date"
              value={schedule.date}
              onChange={handleChange}
              required
              min={new Date().toISOString().split('T')[0]}
              disabled={!schedule.studentId}
            />
          </div>
          
          <div className="form-group">
            <label>Select Time*</label>
            <select
              className="time-select-dropdown"
              value={schedule.time}
              onChange={(e) => handleTimeChange(e.target.value)}
              disabled={!schedule.studentId}
            >
              {timeOptions.map((time, index) => (
                <option key={index} value={time}>
                  {time}
                </option>
              ))}
            </select>
          </div>
          
          <button 
            type="submit" 
            className="submit-btn"
            disabled={!schedule.studentId || !schedule.date}
          >
            Schedule Demo
          </button>
        </form>
        
        {schedule.date && schedule.studentId && (
          <div className="confirmation-message">
            <p>Demo will be scheduled for:</p>
            <p className="selected-student">
              Student ID: {schedule.studentId}
            </p>
            <p className="selected-date">
              {new Date(schedule.date).toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="selected-time">at {schedule.time}</p>
          </div>
        )}
      </div>

      {/* Footer */}
      <Footer />
    </>
  );
};

export default DemoAdmin;